/**
 * 🚙 FLEET MANAGER MONITORING PAGE
 * Monitor fleet manager compliance with vehicle requirements
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Tab,
  Tabs,
  Avatar,
  Paper,
  Divider,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Schedule,
  Refresh,
  Settings,
  Extension,
  TrendingDown,
  Send,
  DirectionsCar,
  CalendarToday,
  Email,
  Phone,
} from '@mui/icons-material';

import { fleetManagerService, type FleetManager } from '@/services/fleetManagerService';

type StatusFilter = 'all' | 'compliant' | 'at_risk' | 'non_compliant';

export const FleetManagerMonitoringPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fleetManagers, setFleetManagers] = useState<FleetManager[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showCriteriaDialog, setShowCriteriaDialog] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    compliant: 0,
    atRisk: 0,
    nonCompliant: 0
  });
  const [criteria, setCriteria] = useState({
    minVehicles: 5,
    verificationPeriodDays: 7
  });

  // Manual intervention dialogs
  const [extensionDialog, setExtensionDialog] = useState<{
    open: boolean;
    hostId: string;
    hostName: string;
    extensionDays: number;
    reason: string;
  }>({
    open: false,
    hostId: '',
    hostName: '',
    extensionDays: 3,
    reason: ''
  });

  const [downgradeDialog, setDowngradeDialog] = useState<{
    open: boolean;
    hostId: string;
    hostName: string;
    reason: string;
  }>({
    open: false,
    hostId: '',
    hostName: '',
    reason: ''
  });

  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    show: false,
    type: 'info',
    message: ''
  });

  useEffect(() => {
    fetchFleetManagers();
  }, [statusFilter]);

  const fetchFleetManagers = async () => {
    setLoading(true);
    try {
      const response = await fleetManagerService.getFleetManagers({
        status: statusFilter
      });

      if (response.success) {
        setFleetManagers(response.data.fleetManagers);
        setSummary(response.data.summary);
        setCriteria(response.data.criteria);
      } else {
        showAlert('error', response.error || 'Failed to load fleet managers');
      }
    } catch (error) {
      showAlert('error', 'Network error loading fleet managers');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchFleetManagers();
    setRefreshing(false);
  };

  const handleGrantExtension = async () => {
    if (!extensionDialog.reason.trim()) {
      showAlert('error', 'Reason is required');
      return;
    }

    const response = await fleetManagerService.grantExtension(
      extensionDialog.hostId,
      extensionDialog.extensionDays,
      extensionDialog.reason
    );

    if (response.success) {
      showAlert('success', `Extension granted to ${extensionDialog.hostName}`);
      setExtensionDialog({ open: false, hostId: '', hostName: '', extensionDays: 3, reason: '' });
      fetchFleetManagers();
    } else {
      showAlert('error', response.error || 'Failed to grant extension');
    }
  };

  const handleDowngrade = async () => {
    if (!downgradeDialog.reason.trim()) {
      showAlert('error', 'Reason is required');
      return;
    }

    const response = await fleetManagerService.downgradeFleetManager(
      downgradeDialog.hostId,
      downgradeDialog.reason
    );

    if (response.success) {
      showAlert('success', `${downgradeDialog.hostName} downgraded to Individual Host`);
      setDowngradeDialog({ open: false, hostId: '', hostName: '', reason: '' });
      fetchFleetManagers();
    } else {
      showAlert('error', response.error || 'Failed to downgrade');
    }
  };

  const showAlert = (type: 'success' | 'error' | 'info', message: string) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'info', message: '' }), 5000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'success';
      case 'at_risk': return 'warning';
      case 'non_compliant': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant': return <CheckCircle sx={{ fontSize: 20 }} />;
      case 'at_risk': return <Warning sx={{ fontSize: 20 }} />;
      case 'non_compliant': return <ErrorIcon sx={{ fontSize: 20 }} />;
      default: return null;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'compliant': return 'Compliant';
      case 'at_risk': return 'At Risk';
      case 'non_compliant': return 'Non-Compliant';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Fleet Manager Monitoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor fleet manager compliance with vehicle requirements
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setShowCriteriaDialog(true)}
          >
            Criteria Settings
          </Button>
        </Box>
      </Box>

      {/* Alert */}
      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', mr: 2 }}>
                  <DirectionsCar />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {summary.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Fleet Managers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: '#e8f5e9', color: '#4caf50', mr: 2 }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {summary.compliant}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compliant
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: '#fff3e0', color: '#ff9800', mr: 2 }}>
                  <Warning />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {summary.atRisk}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    At Risk
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: '#ffebee', color: '#f44336', mr: 2 }}>
                  <ErrorIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {summary.nonCompliant}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Non-Compliant
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, newValue) => setStatusFilter(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" value="all" />
          <Tab label={`Compliant (${summary.compliant})`} value="compliant" />
          <Tab label={`At Risk (${summary.atRisk})`} value="at_risk" />
          <Tab label={`Non-Compliant (${summary.nonCompliant})`} value="non_compliant" />
        </Tabs>
      </Card>

      {/* Fleet Managers Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
            </Box>
          ) : fleetManagers.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No fleet managers found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Fleet Manager</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Application Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Days Remaining</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vehicles</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fleetManagers.map((manager) => (
                    <TableRow key={manager.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {manager.name}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            {manager.email && (
                              <Tooltip title={manager.email}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {manager.email.substring(0, 20)}...
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                            {manager.phone && (
                              <Tooltip title={manager.phone}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {manager.phone}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatDate(manager.applicationDate)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {manager.daysRemaining !== null ? (
                          <Chip
                            label={`${manager.daysRemaining} days`}
                            size="small"
                            color={
                              manager.daysRemaining > 3 ? 'success' :
                              manager.daysRemaining > 0 ? 'warning' : 'error'
                            }
                            icon={<Schedule />}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {manager.vehiclesOnboarded} / {manager.vehiclesRequired}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((manager.vehiclesOnboarded / manager.vehiclesRequired) * 100, 100)}
                            sx={{
                              mt: 0.5,
                              height: 6,
                              borderRadius: 3,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: manager.vehiclesOnboarded >= manager.vehiclesRequired ? '#4caf50' : '#ff9800'
                              }
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(manager.complianceStatus)}
                          color={getStatusColor(manager.complianceStatus) as any}
                          icon={getStatusIcon(manager.complianceStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Grant Extension">
                            <IconButton
                              size="small"
                              onClick={() => setExtensionDialog({
                                open: true,
                                hostId: manager.id,
                                hostName: manager.name,
                                extensionDays: 3,
                                reason: ''
                              })}
                              disabled={manager.complianceStatus === 'compliant'}
                            >
                              <Extension fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Downgrade to Individual">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDowngradeDialog({
                                open: true,
                                hostId: manager.id,
                                hostName: manager.name,
                                reason: ''
                              })}
                            >
                              <TrendingDown fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Grant Extension Dialog */}
      <Dialog open={extensionDialog.open} onClose={() => setExtensionDialog({ ...extensionDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Time Extension</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Grant additional time for <strong>{extensionDialog.hostName}</strong> to meet vehicle requirements
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Extension Days</InputLabel>
            <Select
              value={extensionDialog.extensionDays}
              label="Extension Days"
              onChange={(e) => setExtensionDialog({ ...extensionDialog, extensionDays: Number(e.target.value) })}
            >
              <MenuItem value={1}>1 day</MenuItem>
              <MenuItem value={3}>3 days</MenuItem>
              <MenuItem value={5}>5 days</MenuItem>
              <MenuItem value={7}>7 days</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reason for Extension"
            multiline
            rows={3}
            value={extensionDialog.reason}
            onChange={(e) => setExtensionDialog({ ...extensionDialog, reason: e.target.value })}
            placeholder="e.g., Host experiencing logistics delays..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtensionDialog({ ...extensionDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleGrantExtension} variant="contained" startIcon={<Extension />}>
            Grant Extension
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Dialog */}
      <Dialog open={downgradeDialog.open} onClose={() => setDowngradeDialog({ ...downgradeDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Downgrade to Individual Host</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will downgrade <strong>{downgradeDialog.hostName}</strong> to Individual Host status.
            Their vehicles will remain active.
          </Alert>
          <TextField
            fullWidth
            label="Reason for Downgrade"
            multiline
            rows={3}
            value={downgradeDialog.reason}
            onChange={(e) => setDowngradeDialog({ ...downgradeDialog, reason: e.target.value })}
            placeholder="e.g., Failed to meet minimum vehicle requirement..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDowngradeDialog({ ...downgradeDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleDowngrade} variant="contained" color="error" startIcon={<TrendingDown />}>
            Downgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Criteria Configuration Dialog - Placeholder */}
      <Dialog open={showCriteriaDialog} onClose={() => setShowCriteriaDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Fleet Manager Criteria Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure fleet manager requirements (Coming Soon)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Minimum Vehicles Required"
                type="number"
                value={criteria.minVehicles}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Verification Period (days)"
                type="number"
                value={criteria.verificationPeriodDays}
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCriteriaDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FleetManagerMonitoringPage;
